import { useEffect, useMemo, useState } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import Paper from '@mui/material/Paper';
import { Box, Container, Skeleton } from '@mui/material';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { Link, useSearchParams } from 'react-router-dom';
import CustomText from 'components/common/CustomText/CustomText';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import CustomCellLabel from 'components/tables/AllUsers/CustomCellLabel';
import CustomCellValue from 'components/tables/AllUsers/CustomCellValue';
import StoreOrderStatus from 'components/statuses/StoreOrderStatus';
import FilterIcon from 'icons/FilterIcon';
import { currencies, tableColumnProps } from 'common/constants';
import { dateToString } from 'common/utils';
import { useGetStoreOrders } from 'hooks/useStoreOrders';
import { StoreOrdersFilters as Filters } from 'services/storeOrdersService';
import { colors } from 'styles/colors';
import useGlobalStyles from 'styles/globalStyles';
import StoreOrdersFilters, { EMPTY_STORE_ORDERS_FILTERS } from './StoreOrdersFilters';

const money = (value: number) => `${currencies.shekel}${Number(value ?? 0).toFixed(2)}`;

export default function StoreOrdersTable() {
  const globalStyles = useGlobalStyles();
  // A store's page opens this one as ?storeId=..., already narrowed to it.
  const [searchParams] = useSearchParams();
  const storeIdParam = searchParams.get('storeId') ?? '';

  const initialFilters = useMemo<Filters>(
    () => ({ ...EMPTY_STORE_ORDERS_FILTERS, storeId: storeIdParam }),
    [storeIdParam],
  );

  const [page, setPage] = useState<number>(0);
  const [size, setSize] = useState<number>(10);
  const [rowCount, setRowCount] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(Boolean(storeIdParam));
  const [filters, setFilters] = useState<Filters>(initialFilters);

  useEffect(() => {
    setFilters(initialFilters);
    setPage(0);
  }, [initialFilters]);

  const { data, isLoading, isRefetching } = useGetStoreOrders(
    { page: page + 1, size, ...filters },
    { refetchOnWindowFocus: false, keepPreviousData: true },
  );

  const columns: GridColDef[] = [
    {
      field: 'trackingId',
      ...tableColumnProps,
      renderHeader: () => <CustomCellLabel text="Tracking ID" />,
      renderCell: (params) => <CustomCellValue text={params.row.order?.trackingId ?? '-'} />,
    },
    {
      field: 'store',
      ...tableColumnProps,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => <CustomCellLabel text="Store" />,
      renderCell: (params) => (
        <Link
          to={`/stores/all-stores/${params.row.store?.id}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'none' }}
          onClick={(event) => event.stopPropagation()}
        >
          <CustomText text={params.row.store?.name ?? '-'} fontSize={15} fontWeight={500} color={colors.link} />
        </Link>
      ),
    },
    {
      field: 'customer',
      ...tableColumnProps,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => <CustomCellLabel text="Customer" />,
      renderCell: (params) => <CustomCellValue text={params.row.customer?.fullName ?? '-'} />,
    },
    {
      field: 'itemCount',
      ...tableColumnProps,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => <CustomCellLabel text="Items" />,
      renderCell: (params) => <CustomCellValue text={String(params.row.itemCount ?? 0)} />,
    },
    {
      field: 'total',
      ...tableColumnProps,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => <CustomCellLabel text="Total" />,
      renderCell: (params) => <CustomCellValue text={money(params.row.total)} />,
    },
    {
      field: 'createdAt',
      ...tableColumnProps,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => <CustomCellLabel text="Date" />,
      renderCell: (params) => (
        <CustomCellValue text={params.row.createdAt ? dateToString(new Date(params.row.createdAt.split('T')[0])) : '-'} />
      ),
    },
    {
      field: 'status',
      ...tableColumnProps,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => <CustomCellLabel text="Status" />,
      renderCell: (params) => <StoreOrderStatus status={params.row.status} />,
    },
  ];

  useEffect(() => {
    if (data?.totalCount !== undefined) {
      setRowCount(data.totalCount);
    }
  }, [data?.totalCount]);

  return (
    <Container disableGutters maxWidth={'xl'}>
      <Paper className={globalStyles.tableContainer} sx={{ overflowX: 'auto', minHeight: 11 * 52 + 56 }}>

        {/* Header */}
        <Box
          display={{ sm: 'block', md: 'flex' }}
          justifyContent={'space-between'}
          gap={2}
          sx={{ px: 2, py: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                backgroundColor: colors.topBackground,
                display: 'inline-flex',
                height: 40,
                width: 40,
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '8px',
              }}
            >
              <ReceiptLongOutlinedIcon sx={{ fontSize: 24, color: colors.primary }} />
            </Box>
            <CustomText text="Store Orders" fontSize={16} color={colors.primary} fontWeight={600} />
          </Box>
          <Box display={'flex'} gap={2}>
            <CustomButton
              text='Filter'
              variant='outlined'
              height={40}
              icon={<FilterIcon />}
              onClick={() => setFiltersOpen((prev) => !prev)}
            />
          </Box>
        </Box>

        <StoreOrdersFilters
          open={filtersOpen}
          initialValues={filters}
          loading={isLoading || isRefetching}
          onApply={(values) => {
            setFilters(values);
            setPage(0);
          }}
          onReset={() => {
            setFilters(EMPTY_STORE_ORDERS_FILTERS);
            setPage(0);
          }}
        />

        {/* Data */}
        <DataGrid
          sx={{ minHeight: 11 * 52 + 56 }}
          disableColumnMenu
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          disableColumnSorting
          disableRowSelectionOnClick
          rows={data?.data || []}
          columns={columns}
          loading={isLoading || isRefetching}
          slotProps={{
            loadingOverlay: {
              children: Array.from({ length: 10 }).map((_, i) => (
                <Skeleton key={i} height={52} />
              )),
            },
          }}
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={{ page, pageSize: size }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setSize(model.pageSize);
          }}
          pageSizeOptions={[10, 20, 30, 50, 100]}
          className={globalStyles.dataGrid}
        />
      </Paper>

      <Box mt={3} />
    </Container>
  );
}
